import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { Assignment, Person, Schedule, Send, Upload } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import { saveAs } from 'file-saver';

// Use the API client's configured base URL for any direct fetches (like file download)
const getBaseURL = (): string => {
  const configured = (api.defaults.baseURL as string) || '';
  if (configured) return configured;
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:8000';
    }
    return 'https://capstone-worker.tp071695.workers.dev';
  }
  return 'https://capstone-worker.tp071695.workers.dev';
};

const API_ROOT = getBaseURL();

interface ProjectFile {
  id: number;
  filename: string;
  file_path: string;
  file_type?: string;
  uploaded_by: number;
  uploaded_at: string;
}

interface Project {
  id: number;
  title: string;
  description?: string;
  status: string;
  student_name?: string;
  supervisor_name?: string;
  created_at: string;
  due_date?: string;
  submission_date?: string;
  files?: ProjectFile[];
}

interface ProjectComment {
  id: number;
  comment: string;
  full_name: string;
  role: string;
  created_at: string;
}

interface TeamMember {
  id?: number;
  user_id: number;
  full_name: string;
  user_role: string;
  role: string;
}

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [reviewDecision, setReviewDecision] = useState<'approved' | 'rejected' | 'in_progress'>('approved');
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [acknowledgeFinal, setAcknowledgeFinal] = useState(false);

  const { data: project, isError: projectError, error: projectErr } = useQuery<Project>(
    ['project', id],
    async () => {
      const response = await api.get(`/projects/${id}`);
      return response.data.project;
    },
    {
      enabled: !!id,
      retry: false,
      onError: (err: any) => {
        const status = err?.response?.status;
        if (status === 403) {
          enqueueSnackbar('Access denied for this project', { variant: 'warning' });
        } else if (status === 404) {
          enqueueSnackbar('Project not found', { variant: 'error' });
        } else {
          enqueueSnackbar('Failed to load project', { variant: 'error' });
        }
      }
    }
  );

  const { data: comments = [] } = useQuery<ProjectComment[]>(
    ['project-comments', id],
    async () => {
      const response = await api.get(`/projects/${id}/comments`);
      return response.data.comments;
    },
    { enabled: !!id && !!project, retry: false }
  );

  const { data: files = [] } = useQuery<ProjectFile[]>(
    ['project-files', id],
    async () => {
      const response = await api.get(`/projects/${id}/files`);
      return response.data.files;
    },
    { enabled: !!id && !!project, retry: false }
  );

  // Fetch team members so team students can submit as well
  const { data: teamMembers = [] } = useQuery<TeamMember[]>(
    ['team-members', id],
    async () => {
      const response = await api.get(`/teams/project/${id}`);
      // Hide supervisors from list; we only care about student team members
      return (response.data.teamMembers || []).filter((m: any) => m.user_role !== 'supervisor');
    },
    { enabled: !!id && !!project, retry: false }
  );
  const handleDownload = async (file: ProjectFile) => {
    try {
      const url = `${API_ROOT}${file.file_path}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      const blob = await res.blob();
      saveAs(blob, file.filename);
    } catch (e) {
      enqueueSnackbar('Failed to download file', { variant: 'error' });
    }
  };


  const addCommentMutation = useMutation(
    async (comment: string) => {
      await api.post(`/projects/${id}/comments`, { comment });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['project-comments', id]);
        setNewComment('');
        enqueueSnackbar('Comment added!', { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to add comment', { variant: 'error' });
      },
    }
  );

  const submitProjectMutation = useMutation(
    async () => {
      await api.put(`/projects/${id}`, { status: 'submitted' });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['project', id]);
        enqueueSnackbar('Project submitted successfully!', { variant: 'success' });
        setSubmitDialogOpen(false);
        setAcknowledgeFinal(false);
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to submit project', { variant: 'error' });
      },
    }
  );

  const reviewMutation = useMutation(
    async ({ comment, status }: { comment: string; status: string }) => {
      await api.post(`/projects/${id}/comments`, { comment });
      await api.put(`/projects/${id}`, { status });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['project', id]);
        queryClient.invalidateQueries(['project-comments', id]);
        // Refresh performance dashboards so metrics reflect latest review/status
        queryClient.invalidateQueries('rbac-performance');
        queryClient.invalidateQueries('dashboard-data');
        enqueueSnackbar('Review submitted', { variant: 'success' });
        setReviewOpen(false);
        setReviewText('');
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to submit review', { variant: 'error' });
      }
    }
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'approved':
        return 'info';
      case 'rejected':
        return 'error';
      case 'submitted':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const canSubmitProject = () => {
    // Only students can submit
    if (user?.role !== 'student') return false;
    // Allow if assigned student OR part of the project team
    const isAssigned = project?.student_name === user.full_name;
    const isTeamMember = Array.isArray(teamMembers) && teamMembers.some(m => m.user_id === (user?.id || -1));
    if (!isAssigned && !isTeamMember) return false;
    // Allow submit if status is 'draft', 'in_progress', or 'rejected' (resubmission)
    return project?.status === 'draft' || project?.status === 'in_progress' || project?.status === 'rejected';
  };


  if (projectError) {
    const status = (projectErr as any)?.response?.status;
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {status === 403 ? 'Access Denied' : status === 404 ? 'Project Not Found' : 'Error'}
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/projects')}>Back to Projects</Button>
      </Box>
    );
  }

  if (!project) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{project.title}</Typography>
        <Button variant="outlined" onClick={() => navigate('/projects')}>
          Back to Projects
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Project Details */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Project Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body1" paragraph>
                  {project.description || 'No description provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Person sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    <strong>Student:</strong> {project.student_name || 'Not assigned'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Assignment sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    <strong>Supervisor:</strong> {project.supervisor_name || 'Not assigned'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Schedule sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    <strong>Due Date:</strong> {project.due_date ? new Date(project.due_date).toLocaleDateString() : 'Not set'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip
                    label={getStatusLabel(project.status)}
                    color={getStatusColor(project.status) as any}
                  />
                </Box>
              </Grid>
            </Grid>

    {/* Supervisor Review Dialog */}
    <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Submit Review</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Comment"
            multiline
            minRows={3}
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            placeholder="Provide feedback for the student..."
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={reviewDecision}
              onChange={(e) => setReviewDecision(e.target.value as any)}
            >
              <MenuItem value="approved">Approve</MenuItem>
              <MenuItem value="rejected">Reject</MenuItem>
              <MenuItem value="in_progress">Request Changes</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setReviewOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={() => reviewMutation.mutate({ comment: reviewText || 'Reviewed', status: reviewDecision })}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>

    {/* Submit Final Files Dialog */}
    <Dialog open={submitDialogOpen} onClose={() => setSubmitDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Submit Final Files</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Are you sure you want to submit your final files? After submission, further edits may be restricted and your supervisor will be notified.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2">Files attached: {files ? files.length : 0}</Typography>
          {(!files || files.length === 0) && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              You need to upload at least one file before submitting.
            </Alert>
          )}
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={acknowledgeFinal}
              onChange={(e) => setAcknowledgeFinal(e.target.checked)}
            />
          }
          label="I confirm these are my final files and I'm ready to submit."
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSubmitDialogOpen(false)}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!acknowledgeFinal || !files || files.length === 0 || submitProjectMutation.isLoading}
          onClick={() => submitProjectMutation.mutate()}
        >
          {submitProjectMutation.isLoading ? 'Submitting...' : 'Confirm Submit'}
        </Button>
      </DialogActions>
    </Dialog>
          </Paper>

          {/* Comments */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Comments</Typography>
              {(user?.role === 'admin' || user?.role === 'supervisor') && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={reviewDecision}
                      onChange={(e) => {
                        const next = e.target.value as any;
                        setReviewDecision(next);
                        // Auto-submit status changes so the chip updates immediately
                        reviewMutation.mutate({ comment: newComment || 'Reviewed', status: next });
                      }}
                    >
                      <MenuItem value="approved">Approve</MenuItem>
                      <MenuItem value="rejected">Reject</MenuItem>
                      <MenuItem value="in_progress">Request Changes</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
            <List>
              {comments.map((comment) => (
                <ListItem key={comment.id} alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar>
                      <Person />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">{comment.full_name}</Typography>
                        <Chip label={comment.role} size="small" />
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {comment.comment}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(comment.created_at).toLocaleString()}
                        </Typography>
                      </>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))}
            </List>

            {/* Add Comment */}
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Add a comment"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={() => addCommentMutation.mutate(newComment)}
                disabled={!newComment.trim()}
              >
                Add Comment
              </Button>
            </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Project Info
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Created: {new Date(project.created_at).toLocaleDateString()}
              </Typography>
              {project.submission_date && (
                <Typography variant="body2" color="text.secondary">
                  Submitted: {new Date(project.submission_date).toLocaleDateString()}
                </Typography>
              )}
            </Box>
            <Divider sx={{ my: 2 }} />
            {user?.role === 'student' && (
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {canSubmitProject() && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setSubmitDialogOpen(true)}
                  disabled={submitProjectMutation.isLoading}
                  fullWidth
                >
                  {submitProjectMutation.isLoading ? 'Submitting...' : (project.status === 'rejected' ? 'Resubmit Project' : 'Submit Project')}
                </Button>
              )}
              {/* Supervisor quick action removed per request */}
              {user?.role === 'student' && (
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<Upload />}
                  fullWidth
                  disabled={isUploading}
                >
                  {isUploading ? `Uploading... ${uploadProgress}%` : 'Upload Files'}
                  <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    multiple
                    onChange={async (e) => {
                    const files = e.target.files;
                    if (!files || files.length === 0) return;
                    const maxSizeBytes = 20 * 1024 * 1024;
                    for (const f of Array.from(files)) {
                      if (f.size > maxSizeBytes) {
                        enqueueSnackbar(`${f.name} exceeds 20MB limit`, { variant: 'error' });
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                        return;
                      }
                    }
                    const form = new FormData();
                    Array.from(files).forEach(f => form.append('files', f));
                    try {
                      setIsUploading(true);
                      setUploadProgress(0);
                      const resp = await api.post(`/projects/${id}/files`, form, {
                        onUploadProgress: (evt) => {
                          if (!evt.total) return;
                          const pct = Math.round((evt.loaded * 100) / evt.total);
                          setUploadProgress(pct);
                        },
                      });
                      enqueueSnackbar('Files uploaded!', { variant: 'success' });
                      // Optimistically update cache so files appear immediately
                      const uploaded = (resp?.data?.files || []).map((f: any) => ({
                        id: f.id ?? Math.random(),
                        filename: f.filename,
                        file_path: f.file_path,
                        file_type: f.file_type,
                        uploaded_by: user?.id ?? 0,
                        uploaded_at: new Date().toISOString()
                      }));
                      queryClient.setQueryData<ProjectFile[] | undefined>(['project-files', id], (prev) => {
                        const existing = Array.isArray(prev) ? prev : [];
                        // Prepend newest first
                        return [...uploaded, ...existing];
                      });
                      // Still invalidate to reconcile with server truth
                      queryClient.invalidateQueries(['project', id]);
                      queryClient.invalidateQueries(['project-files', id]);
                    } catch (err: any) {
                      console.error('Upload error:', err);
                      console.error('Error response:', err.response?.data);
                      enqueueSnackbar(err.response?.data?.message || 'Upload failed', { variant: 'error' });
                    } finally {
                      setIsUploading(false);
                      setUploadProgress(0);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }
                    }}
                  />
                </Button>
              )}

              <Box>
                <Typography variant="subtitle1" gutterBottom>Files</Typography>
                {files && files.length > 0 ? (
                  <List>
                    {files.map(f => (
                      <ListItem key={f.id} button onClick={() => handleDownload(f)}>
                        <ListItemAvatar>
                          <Avatar>
                            <Upload />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={f.filename}
                          secondary={f.uploaded_at ? new Date(f.uploaded_at).toLocaleString() : ''}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No files uploaded yet.</Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectDetail; 